import React, { useMemo } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import { formatCurrencyGBP } from '../../utils/formatNumbers';
import { useGetJobQuery, useGetJobOrdersQuery } from '../../store/slices/apiSlice';
import type { OrderSummary } from '../../utils/types';
import type { JobDetailsPageProps } from '../../types';
import { OrderDetailsTable } from '../../components/common/OrderDetailsTable';
import { ClipboardList, ArrowLeft, Shield, AlertTriangle } from 'lucide-react';

export const JobDetailsPage: React.FC<JobDetailsPageProps> = ({ jobId: propJobId }) => {
  const { id } = useParams<{ id: string }>();
  const jobId = propJobId || id;
  const location = useLocation();
  const navigate = useNavigate();

  const { data: job, isLoading, isError, error } = useGetJobQuery(jobId || '', { skip: !jobId });

  const headerSubtitle = useMemo(() => {
    if (!job) return '';
    const created = new Date(job.created_at).toLocaleString();
    const by = job.created_by_username ? `by ${job.created_by_username}` : '';
    return `${job.website_job_number || '-'} • ${created} ${by}`;
  }, [job]);

  const parsedNotes = useMemo(() => {
    const list = (job?.admin_notes_list && job.admin_notes_list.length > 0) ? job.admin_notes_list : null;
    let items = [] as Array<{ timestamp: string | null; author: string | null; text: string }>;
    if (list) {
      items = list.map(n => ({ timestamp: n.timestamp || null, author: n.username || null, text: n.text }));
    } else {
      const lines = (job?.admin_notes || '').split('\n').filter(l => l.trim() !== '');
      items = lines.map(lineRaw => {
        const line = lineRaw.trim();
        const match = line.match(/^\[(.*?)\]\s+([^:]+):\s*(.*)$/);
        if (match) {
          return { timestamp: match[1], author: match[2], text: match[3] };
        }
        return { timestamp: null, author: null, text: line };
      });
    }
    const withTs = items.filter(i => i.timestamp);
    const withoutTs = items.filter(i => !i.timestamp);
    withTs.sort((a, b) => {
      const ta = new Date(a.timestamp as string).getTime();
      const tb = new Date(b.timestamp as string).getTime();
      return tb - ta;
    });
    return [...withTs, ...withoutTs];
  }, [job?.admin_notes_list, job?.admin_notes]);

  // Tabs
  const isOrdersRoute = location.pathname.endsWith('/orders');
  const [activeTab, setActiveTab] = React.useState<'overview' | 'orders'>(isOrdersRoute ? 'orders' : 'overview');
  React.useEffect(() => { setActiveTab(isOrdersRoute ? 'orders' : 'overview'); }, [isOrdersRoute]);

  const OrdersPanel: React.FC<{ jobId: string }> = ({ jobId }) => {
    const { data: ordersData } = useGetJobOrdersQuery(jobId);
    const orders: OrderSummary[] = Array.isArray(ordersData) ? ordersData : (ordersData?.results ?? []);

    return (
      <ContentCard title="Orders (View Only)">
        <div className="text-sm text-gray-600 mb-3">To create, edit, or delete orders, use Edit Job.</div>
        <OrderDetailsTable
          orders={orders}
          loading={false}
          mode="view"
          rackContext="job_details"
          className="mt-3"
        />
      </ContentCard>
    );
  };

  if (isLoading) {
    return (
      <AppShell>
        <PageLayout
          title="Job Details"
          subtitle="Loading job..."
          bgColor={PAGE_COLORS.jobs.bgColor}
          textColor={PAGE_COLORS.jobs.textColor}
          borderColor={PAGE_COLORS.jobs.borderColor}
          icon={<ClipboardList className="w-6 h-6" />}
        >
          <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
        </PageLayout>
      </AppShell>
    );
  }

  if (isError || !job) {
    const is403 = 'status' in (error as any) && (error as any).status === 403;
    return (
      <AppShell>
        <PageLayout
          title="Job Details"
          subtitle="Unable to load job"
          bgColor={PAGE_COLORS.jobs.bgColor}
          textColor={PAGE_COLORS.jobs.textColor}
          borderColor={PAGE_COLORS.jobs.borderColor}
          icon={is403 ? <Shield className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
        >
          <div className="mb-4">
            <Link to="/jobs"><Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Back to Jobs</Button></Link>
          </div>
          <ContentCard title={is403 ? 'Access Denied' : 'Error Loading Job'}>
            <div className="text-gray-700">{is403 ? "You don't have permission to view this job." : 'Please try again later.'}</div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  const lastUpdatedBy = (job as any).updated_by_username || job.created_by_username;

  return (
    <AppShell>
      <PageLayout
        title={`Job Details`}
        subtitle={headerSubtitle}
        bgColor={PAGE_COLORS.jobs.bgColor}
        textColor={PAGE_COLORS.jobs.textColor}
        borderColor={PAGE_COLORS.jobs.borderColor}
        icon={<ClipboardList className="w-6 h-6" />}
      >
        {/* Top Tabs with Back button on the right */}
        <div className="mb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <nav className="-mb-px flex gap-4" aria-label="Tabs">
              <button
                className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`}
                onClick={() => navigate(`/jobs/${id}`)}
              >Overview</button>
              <button
                className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${activeTab === 'orders' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`}
                onClick={() => navigate(`/jobs/${id}/orders`)}
              >Orders</button>
            </nav>
            <Link to="/jobs"><Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Back to Jobs</Button></Link>
          </div>
        </div>

        {activeTab === 'overview' && (
        <>
        {/* Customer Details */}
        <ContentCard title="Customer Details">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-500">Name</div>
              <div className="text-gray-900">{job.customer_first_name} {job.customer_last_name}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Email</div>
              <div className="text-gray-900">{job.customer_email || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Mobile</div>
              <div className="text-gray-900">{job.customer_mobile_number || '-'}</div>
            </div>
          </div>
        </ContentCard>

        {/* Addresses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <ContentCard title="Delivery Address">
            <div className="text-gray-900">{job.delivery_address_line1}</div>
            {job.delivery_address_line2 && <div className="text-gray-900">{job.delivery_address_line2}</div>}
            {job.delivery_city && <div className="text-gray-900">{job.delivery_city}</div>}
            <div className="text-gray-900">{job.delivery_postcode}</div>
            {job.delivery_notes && <div className="text-gray-600 mt-2">{job.delivery_notes}</div>}
          </ContentCard>
          <ContentCard title="Invoice Address">
            <div className="text-gray-900">{job.invoice_address_line1}</div>
            {job.invoice_address_line2 && <div className="text-gray-900">{job.invoice_address_line2}</div>}
            {job.invoice_city && <div className="text-gray-900">{job.invoice_city}</div>}
            <div className="text-gray-900">{job.invoice_postcode}</div>
          </ContentCard>
        </div>

        {/* Order Summary */}
        <div className="mt-4">
          <ContentCard title="Order Summary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500">Total Items</div>
                <div className="text-gray-900">{job.total_items}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Amount</div>
                <div className="text-gray-900">{formatCurrencyGBP(job.total_amount)}</div>
              </div>
            </div>
          </ContentCard>
        </div>

        {/* Website Meta */}
        <div className="mt-4">
          <ContentCard title="Website Metadata">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500">Source</div>
                <div className="text-gray-900 capitalize">{job.source_type}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Website Job #</div>
                <div className="text-gray-900">{job.website_job_number || '-'}</div>
              </div>
            </div>
          </ContentCard>
        </div>

        {parsedNotes.length > 0 && (
          <div className="mt-4">
            <ContentCard title="Admin Notes">
              <div className="text-xs text-gray-600 mb-2">
                {lastUpdatedBy ? (
                  <>Last updated by <span className="font-medium">{lastUpdatedBy}</span> at {new Date(job.updated_at).toLocaleString()}</>
                ) : (
                  <>Last updated at {new Date(job.updated_at).toLocaleString()}</>
                )}
              </div>
              <ul className="space-y-2">
                {parsedNotes.map((n, idx) => (
                  <li key={idx} className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <div className="text-xs text-gray-600 mb-1">
                      {n.timestamp && n.author ? (
                        <>
                          {n.timestamp} • {n.author}
                        </>
                      ) : (
                        <>Legacy note (author unknown)</>
                      )}
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{n.text}</div>
                  </li>
                ))}
              </ul>
            </ContentCard>
          </div>
        )}
        </>
        )}

        {activeTab === 'orders' && (
          <div className="mt-2">
            <OrdersPanel jobId={id!} />
          </div>
        )}
      </PageLayout>
    </AppShell>
  );
};
